import { validateNumeric2DArray, validateFitted, validatePositiveInteger } from '../utils/validation.js';
import { euclideanDistance } from '../utils/matrix.js';
import { randomInt } from '../utils/random.js';

export class KMeans {
  constructor(options = {}) {
    this.nClusters = options.nClusters || 3;
    validatePositiveInteger(this.nClusters, 'nClusters');

    this.maxIterations = options.maxIterations || 300;
    this.tolerance = options.tolerance || 0.0001;
    this.initMethod = options.initMethod || 'kmeans++';
    this.nInit = options.nInit || 10;

    this.centroids = null;
    this.labels = null;
    this.inertia = null;
    this.nIterations = 0;
    this.isFitted = false;
  }

  _initializeCentroidsRandom(X) {
    const indices = new Set();
    while (indices.size < this.nClusters) {
      indices.add(randomInt(0, X.length - 1));
    }
    return Array.from(indices).map(i => [...X[i]]);
  }

  _initializeCentroidsKMeansPlusPlus(X) {
    const centroids = [];

    const firstIndex = randomInt(0, X.length - 1);
    centroids.push([...X[firstIndex]]);

    for (let i = 1; i < this.nClusters; i++) {
      const distances = X.map(point => {
        const dists = centroids.map(c => Math.pow(euclideanDistance(point, c), 2));
        const minDist = Math.min.apply(null, dists);
        return minDist;
      });

      const totalDist = distances.reduce((a, b) => a + b, 0);
      const probabilities = distances.map(d => d / totalDist);

      let cumSum = 0;
      const rand = Math.random();
      for (let j = 0; j < probabilities.length; j++) {
        cumSum += probabilities[j];
        if (rand <= cumSum) {
          centroids.push([...X[j]]);
          break;
        }
      }
    }

    return centroids;
  }

  _assignClusters(X, centroids) {
    return X.map(point => {
      const distances = centroids.map(c => euclideanDistance(point, c));
      return distances.indexOf(Math.min.apply(null, distances));
    });
  }

  _updateCentroids(X, labels) {
    const newCentroids = Array(this.nClusters).fill(0).map(() =>
      Array(X[0].length).fill(0)
    );
    const counts = Array(this.nClusters).fill(0);

    for (let i = 0; i < X.length; i++) {
      const cluster = labels[i];
      counts[cluster]++;
      for (let j = 0; j < X[i].length; j++) {
        newCentroids[cluster][j] += X[i][j];
      }
    }

    for (let i = 0; i < this.nClusters; i++) {
      if (counts[i] > 0) {
        for (let j = 0; j < newCentroids[i].length; j++) {
          newCentroids[i][j] /= counts[i];
        }
      }
    }

    return newCentroids;
  }

  _calculateInertia(X, labels, centroids) {
    let inertia = 0;
    for (let i = 0; i < X.length; i++) {
      const centroid = centroids[labels[i]];
      inertia += Math.pow(euclideanDistance(X[i], centroid), 2);
    }
    return inertia;
  }

  _fitOnce(X) {
    let centroids;
    if (this.initMethod === 'kmeans++') {
      centroids = this._initializeCentroidsKMeansPlusPlus(X);
    } else {
      centroids = this._initializeCentroidsRandom(X);
    }

    let labels = null;
    let iterations = 0;

    for (let iter = 0; iter < this.maxIterations; iter++) {
      iterations = iter + 1;

      labels = this._assignClusters(X, centroids);

      const newCentroids = this._updateCentroids(X, labels);

      let maxShift = 0;
      for (let i = 0; i < this.nClusters; i++) {
        const shift = euclideanDistance(centroids[i], newCentroids[i]);
        maxShift = Math.max(maxShift, shift);
      }

      centroids = newCentroids;

      if (maxShift < this.tolerance) {
        break;
      }
    }

    const inertia = this._calculateInertia(X, labels, centroids);

    return { centroids, labels, inertia, iterations };
  }

  fit(X) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    if (X.length < this.nClusters) {
      throw new Error(`Number of samples (${X.length}) must be >= number of clusters (${this.nClusters})`);
    }

    let bestResult = null;
    let bestInertia = Infinity;

    for (let i = 0; i < this.nInit; i++) {
      const result = this._fitOnce(X);

      if (result.inertia < bestInertia) {
        bestInertia = result.inertia;
        bestResult = result;
      }
    }

    this.centroids = bestResult.centroids;
    this.labels = bestResult.labels;
    this.inertia = bestResult.inertia;
    this.nIterations = bestResult.iterations;

    this.isFitted = true;
    return this;
  }

  predict(X) {
    validateFitted(this.isFitted, 'KMeans');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return this._assignClusters(X, this.centroids);
  }

  fitPredict(X) {
    this.fit(X);
    return this.labels;
  }

  transform(X) {
    validateFitted(this.isFitted, 'KMeans');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(point =>
      this.centroids.map(c => euclideanDistance(point, c))
    );
  }
}
